import React, { createContext, useContext, useState, useEffect } from 'react';
import { MaintenanceReport } from '@/models/maintenance';
import { 
  getPendingMaintenanceReports, 
  getCompletedMaintenanceReports,
  markMaintenanceReportAsDismissed
} from '@/services/maintenanceService';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

// Clave para almacenar las notificaciones descartadas en localStorage
const DISMISSED_NOTIFICATIONS_KEY = 'dismissed_maintenance_notifications';

export interface MaintenanceNotification {
  id: string;
  screenId: string;
  screenName: string;
  screenLocation?: string;
  problems: string[];
  details?: string;
  createdAt: string;
  completedAt?: string;
  estimatedTimeHours: number;
  read: boolean;
  completed: boolean;
  dismissed?: boolean;
}

interface NotificationContextType {
  notifications: MaintenanceNotification[];
  completedNotifications: MaintenanceNotification[];
  unreadCount: number;
  markAsRead: (id: string) => void;
  clearNotifications: () => void;
  refreshNotifications: () => Promise<void>;
  addCompletedNotification: (notification: MaintenanceNotification) => void;
  dismissCompletedNotification: (id: string) => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<MaintenanceNotification[]>([]);
  const [completedNotifications, setCompletedNotifications] = useState<MaintenanceNotification[]>([]);
  const [dismissedNotificationIds, setDismissedNotificationIds] = useState<string[]>([]);
  const { isAdmin, isSeller } = useAuth();

  // Solo mostrar notificaciones a administradores y vendedores
  const shouldShowNotifications = isAdmin || isSeller;

  // Cargar IDs de notificaciones descartadas desde localStorage
  useEffect(() => {
    if (shouldShowNotifications) {
      const savedDismissedIds = localStorage.getItem(DISMISSED_NOTIFICATIONS_KEY);
      if (savedDismissedIds) {
        try {
          const parsedIds = JSON.parse(savedDismissedIds);
          if (Array.isArray(parsedIds)) {
            setDismissedNotificationIds(parsedIds);
          }
        } catch (error) {
          console.error('Error al cargar notificaciones descartadas:', error);
        }
      }
    }
  }, [shouldShowNotifications]);

  // Cargar notificaciones al iniciar
  useEffect(() => {
    if (shouldShowNotifications) {
      refreshNotifications();
      
      // Actualizar cada 5 minutos
      const interval = setInterval(() => {
        refreshNotifications();
      }, 5 * 60 * 1000);
      
      return () => clearInterval(interval);
    }
  }, [shouldShowNotifications, dismissedNotificationIds]);

  // Función para refrescar notificaciones
  const refreshNotifications = async () => {
    if (!shouldShowNotifications) return;
    
    try {
      // Obtener reportes pendientes y completados
      const pendingReports = await getPendingMaintenanceReports();
      const completedReports = await getCompletedMaintenanceReports();
      
      // Convertir reportes pendientes a notificaciones
      const pendingNotifications = pendingReports
        .filter(report => !report.isDismissed) // Filtrar reportes ya descartados
        .map(report => {
          // Buscar si ya existe esta notificación
          const existingNotification = notifications.find(n => n.id === report.id);
          
          return {
            id: report.id,
            screenId: report.screenId,
            screenName: report.screen?.name || `Pantalla ${report.screenId}`,
            screenLocation: report.screen?.location,
            problems: report.problems,
            details: report.details,
            createdAt: report.createdAt,
            completedAt: report.completedAt,
            estimatedTimeHours: report.estimatedTimeHours,
            read: existingNotification?.read || false,
            completed: false,
            dismissed: report.isDismissed
          };
        });
      
      // Convertir reportes completados a notificaciones
      // Solo mostrar en notificaciones los que NO están descartados
      const newCompletedNotifications = completedReports
        .filter(report => !report.isDismissed) // Importante: solo mostrar los no descartados como notificaciones
        .map(report => {
          // Buscar si ya existe esta notificación
          const existingNotification = completedNotifications.find(n => n.id === report.id);
          
          return {
            id: report.id,
            screenId: report.screenId,
            screenName: report.screen?.name || `Pantalla ${report.screenId}`,
            screenLocation: report.screen?.location,
            problems: report.problems,
            details: report.details || '',
            createdAt: report.createdAt,
            completedAt: report.completedAt || null,
            estimatedTimeHours: report.estimatedTimeHours,
            read: existingNotification?.read || false,
            completed: true,
            dismissed: report.isDismissed
          } as MaintenanceNotification;
        });
      
      // Actualizar las notificaciones
      setNotifications(pendingNotifications);
      
      // Actualizar las notificaciones completadas
      setCompletedNotifications(newCompletedNotifications);
    } catch (error) {
      console.error("Error al cargar notificaciones de mantenimiento:", error);
    }
  };

  // Marcar una notificación como leída
  const markAsRead = (id: string) => {
    setNotifications(prev => 
      prev.map(notification => 
        notification.id === id 
          ? { ...notification, read: true } 
          : notification
      )
    );
    
    setCompletedNotifications(prev => 
      prev.map(notification => 
        notification.id === id 
          ? { ...notification, read: true } 
          : notification
      )
    );
  };

  // Limpiar todas las notificaciones
  const clearNotifications = () => {
    setNotifications([]);
    setCompletedNotifications([]);
  };

  // Agregar una notificación de mantenimiento completado
  const addCompletedNotification = (notification: MaintenanceNotification) => {
    // Eliminar la notificación de la lista de pendientes si existe
    setNotifications(prev => prev.filter(n => n.id !== notification.id));
    
    // Verificar si ya existe esta notificación completada
    const existingNotification = completedNotifications.find(n => n.id === notification.id);
    
    if (!existingNotification) {
      // Si no existe, agregar como nueva notificación
      setCompletedNotifications(prev => [
        { ...notification, completed: true, read: false, dismissed: false },
        ...prev
      ]);
    } else {
      // Si existe, actualizar manteniendo el estado de descartado
      setCompletedNotifications(prev =>
        prev.map(n => n.id === notification.id
          ? { ...notification, completed: true, read: false, dismissed: existingNotification.dismissed }
          : n
        )
      );
    }
  };

  // Descartar una notificación completada
  const dismissCompletedNotification = async (id: string) => {
    try {
      // Marcar como descartada en Supabase
      const success = await markMaintenanceReportAsDismissed(id);
      
      if (!success) {
        toast.error('Error al descartar la notificación');
        return;
      }
      
      // Marcar como descartada en el estado local
      setCompletedNotifications(prev => 
        prev.map(notification => 
          notification.id === id 
            ? { ...notification, dismissed: true } 
            : notification
        )
      );
      
      // Filtrar las notificaciones descartadas de la vista
      setCompletedNotifications(prev => 
        prev.filter(notification => !notification.dismissed)
      );
      
    } catch (error) {
      console.error('Error al descartar notificación completada:', error);
      toast.error('Error al descartar la notificación');
    }
  };

  // Calcular notificaciones no leídas
  const unreadCount = notifications.filter(n => !n.read).length;

  const value = {
    notifications,
    completedNotifications,
    unreadCount,
    markAsRead,
    clearNotifications,
    refreshNotifications,
    addCompletedNotification,
    dismissCompletedNotification
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}; 